import type { WorkflowCardLabels, WorkflowLabels } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { useMemo } from "react";

const PREFIX = "chat.workflow";

const STATIC_KEYS = [
  "choose",
  "chosen",
  "submit",
  "skip",
  "select",
  "required",
  "invalidEmail",
  "invalidPhone",
  "invalidNumber",
  "invalidDate",
  "invalidOption",
  "invalidFromServer",
  "expired",
  "privacyLink",
  "typingLocked",
] as const satisfies readonly (keyof WorkflowLabels)[];

const RATING_STATIC_KEYS = ["ratingGroup", "thumbsUp", "thumbsDown", "ratingComment", "ratingSubmit"] as const satisfies readonly (keyof WorkflowCardLabels)[];

const VERIFY_STATIC_KEYS = ["verifyGroup", "sendCode", "resendCode", "resendReady", "securityCheck", "securityCheckWait"] as const satisfies readonly (keyof WorkflowCardLabels)[];

/** Workflow card copy from the shared catalog (en / fil); the chat package keeps English defaults. */
export function useWorkflowCopy(): WorkflowCardLabels {
  const { t } = useTranslation();
  return useMemo<WorkflowCardLabels>(
    () => ({
      ...(Object.fromEntries(STATIC_KEYS.map((key) => [key, t(`${PREFIX}.${key}`)])) as Record<(typeof STATIC_KEYS)[number], string>),
      ...(Object.fromEntries(RATING_STATIC_KEYS.map((key) => [key, t(`${PREFIX}.${key}`)])) as Record<(typeof RATING_STATIC_KEYS)[number], string>),
      ...(Object.fromEntries(VERIFY_STATIC_KEYS.map((key) => [key, t(`${PREFIX}.${key}`)])) as Record<(typeof VERIFY_STATIC_KEYS)[number], string>),
      codeWillGoTo: (email) => t(`${PREFIX}.codeWillGoTo`, { email }),
      codeSentTo: (email) => t(`${PREFIX}.codeSentTo`, { email }),
      codeExpiresAt: (time) => t(`${PREFIX}.codeExpiresAt`, { time }),
      triesLeft: (count) => t(`${PREFIX}.triesLeft`, { count }),
      resendIn: (seconds) => t(`${PREFIX}.resendIn`, { seconds }),
      invalidCode: (length) => t(`${PREFIX}.invalidCode`, { length }),
      articleGroup: (title) => t(`${PREFIX}.articleGroup`, { title }),
      articleNewTab: t(`${PREFIX}.articleNewTab`),
      answeredWith: (value) => t(`${PREFIX}.answeredWith`, { value }),
      replyAnswer: (question) => t(`${PREFIX}.replyAnswer`, { question }),
      replyPlaceholder: t(`${PREFIX}.replyPlaceholder`),
      rateStar: (stars) => t(`${PREFIX}.rateStar`, { n: stars }),
      starsOutOf: (stars) => t(`${PREFIX}.starsOutOf`, { n: stars }),
      ratingCommentCount: (used, max) => t(`${PREFIX}.ratingCommentCount`, { used, max }),
      ratingSelected: (rating) => t(`${PREFIX}.ratingSelected`, { rating }),
    }),
    // `t` changes with the language.
    [t],
  );
}
